import { useEffect } from "react";
import { Link } from "react-router-dom";
import { BasilCheck, BasilGift, BasilPlus } from "@/components/icons/basil";
import { useCollectionData } from "@/components/collection/useCollectionData";
import { useProfile } from "@/components/layout/useProfile";
import { Countdown } from "@/components/renewal/Countdown";
import { useMissions } from "@/lib/renewal/missions";
import { cn } from "@shared/lib/utils";

/**
 * 상시 게임 HUD — 웹게임 헤더 문법(잔액 + 충전 CTA + 무료팩 타이머 + 퀘스트
 * 뱃지, RollerCoin 보너스 타이머·Courtyard 헤더 참조). lg+ 슬림 바 우측과
 * 모바일 TopBar(compact)에서 렌더. 비로그인 시 null — 사인인은 ProfileChip 몫.
 * 잔액은 useProfile 공유 캐시(마운트 시 1회 ensure), 무료팩은 컬렉션 SWR 캐시.
 */
export function HudChips({ compact = false }: { compact?: boolean }) {
  const { authenticated, summary, ensureSummary } = useProfile();
  const { state: cardsState } = useCollectionData();
  const { missions } = useMissions();

  // HUD는 상시 노출이라 지연 페치 전략을 마운트 시 1회로 앞당긴다(캐시·inflight 공유).
  useEffect(() => {
    if (authenticated) ensureSummary();
  }, [authenticated, ensureSummary]);

  if (!authenticated) return null;

  const claimable = missions.filter((mission) => mission.status === "claimable").length;
  const dailyReady = cardsState?.dailyFreeAvailable === true;
  const nextDailyFreeAt = cardsState?.nextDailyFreeAt ?? null;

  const chip =
    "flex h-8 items-center gap-1.5 rounded-full border border-line bg-(--ty-bg-2) px-3 font-mono text-[11px] font-medium tabular-nums text-ink transition-colors duration-200 hover:border-line-hi outline-none focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-accent/60";

  return (
    <div className={cn("flex items-center", compact ? "gap-1" : "gap-1.5")}>
      {/* RP 잔액 + 충전 — 게임 헤더의 [코인 · +] 문법 */}
      <Link
        to="/profile/wallet/topup"
        aria-label={`RP balance ${summary ? summary.rp.toLocaleString("en-US") : "loading"} — top up`}
        className={cn(chip, "pr-1.5")}
      >
        <span>
          {summary ? (
            summary.rp.toLocaleString("en-US")
          ) : (
            <span className="inline-block h-3 w-12 animate-pulse rounded bg-white/10" aria-hidden />
          )}{" "}
          RP
        </span>
        <span className="grid size-5 place-items-center rounded-full bg-(--ty-accent-fill) text-accent">
          <BasilPlus className="size-3" aria-hidden />
        </span>
      </Link>

      {/* 무료팩 — 준비되면 골드 강조, 아니면 카운트다운 */}
      <Link
        to="/profile/packs"
        aria-label={dailyReady ? "Daily free pack ready — open now" : "Next daily free pack timer"}
        className={cn(chip, dailyReady && "border-accent/40 bg-accent/10 text-accent")}
      >
        <BasilGift className="size-3.5 shrink-0" aria-hidden />
        {dailyReady ? (
          <span className="flex items-center gap-1.5">
            {!compact && <span>Free pack</span>}
            <span aria-hidden className="relative flex size-1.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-accent opacity-60 motion-safe:animate-ping" />
              <span className="relative inline-flex size-1.5 rounded-full bg-accent" />
            </span>
          </span>
        ) : nextDailyFreeAt ? (
          <Countdown to={nextDailyFreeAt} className="tabular-nums" />
        ) : (
          <span>—</span>
        )}
      </Link>

      {/* 미션 클레임 뱃지 — 시그널 전용: 받을 게 있을 때만 뜬다(0은 노이즈).
          compact(모바일 바)에서는 생략 — 탭바 Profile 도트가 담당. */}
      {!compact && claimable > 0 && (
        <Link
          to="/profile/missions"
          aria-label={`Missions — ${claimable} rewards to claim`}
          className={cn(chip, "border-danger/40")}
        >
          <BasilCheck className="size-3.5 shrink-0" aria-hidden />
          <span className="rounded-full bg-danger/15 px-1.5 text-[10px] font-semibold text-danger">
            {claimable}
          </span>
        </Link>
      )}
    </div>
  );
}
